import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Users, Target, Zap, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import BirdMascot from '../components/BirdMascot';

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-muted/30 to-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <BirdMascot size={32} />
            <span className="text-xl font-heading font-bold">JailBirds</span>
          </Link>
          
          <Link to="/">
            <Button variant="outline" size="sm" className="gap-2">
              <Home className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-8"
        >
          {/* Hero Section */}
          <div className="text-center mb-12">
            <BirdMascot size={80} className="mx-auto mb-4" />
            <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4 text-gradient-primary">
              About JailBirds
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Test your detective instincts with real arrest records from Mecklenburg County
            </p>
          </div>

          {/* What is JailBirds */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <Target className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-heading font-bold">What is JailBirds?</h2>
            </div>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                JailBirds is an interactive guessing game that challenges players to match real mugshots with their associated criminal charges. Using publicly available arrest records from the Mecklenburg County Sheriff's Office in North Carolina, the game tests your ability to make quick judgments based on visual information.
              </p>
              <p>
                Each game presents players with 11 regular questions plus a special JailBird Bonus round, creating an engaging experience that combines entertainment with real-world data.
              </p>
            </div>
          </div>

          {/* How It Works */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <Zap className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-heading font-bold">How It Works</h2>
            </div>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <ul className="space-y-3">
                <li className="flex gap-3">
                  <span className="text-primary font-bold">1.</span>
                  <span>View a real mugshot from Mecklenburg County arrest records</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary font-bold">2.</span>
                  <span>Choose from four possible charges that match the arrest</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary font-bold">3.</span>
                  <span>Earn points for correct answers and speed bonuses</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary font-bold">4.</span>
                  <span>Compete on global leaderboards and share your scores</span>
                </li>
              </ul>
              <p className="mt-4">
                Our automated scraper updates the game daily with fresh arrest records, ensuring new content and variety for regular players.
              </p>
            </div>
          </div>

          {/* Our Mission */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-heading font-bold">Our Mission</h2>
            </div>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                JailBirds aims to create an engaging gaming experience while maintaining respect for the serious nature of the judicial system. We believe in:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Transparency in public records and government data</li>
                <li>Educational awareness about the diversity of criminal charges</li>
                <li>The presumption of innocence for all individuals featured</li>
                <li>Responsible use of publicly available information</li>
              </ul>
            </div>
          </div>

          {/* Important Notice */}
          <div className="bg-card border border-amber-500/50 rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-6 h-6 text-amber-500" />
              <h2 className="text-2xl font-heading font-bold">Important Notice</h2>
            </div>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p className="text-foreground font-semibold">
                All individuals featured in JailBirds are presumed innocent until proven guilty in a court of law.
              </p>
              <p>
                The arrest records and charges displayed are allegations only and do not constitute proof of guilt. All information is sourced from official public records made available by law enforcement agencies.
              </p>
              <p>
                If you believe information displayed is inaccurate or should be removed, please contact us at{' '}
                <a href="mailto:support@jailbirdsgame.com" className="text-primary hover:underline font-semibold">
                  support@jailbirdsgame.com
                </a>
              </p>
            </div>
          </div>

          {/* Ownership */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-heading font-bold mb-4">Ownership & Contact</h2>
            <div className="space-y-3 text-muted-foreground">
              <p>
                <strong className="text-foreground">JailBirds is owned and operated by M.Panepinto</strong>
              </p>
              <p>
                For questions, feedback, or support inquiries:
              </p>
              <a 
                href="mailto:support@jailbirdsgame.com" 
                className="text-lg font-semibold text-primary hover:underline inline-block"
              >
                support@jailbirdsgame.com
              </a>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center pt-8">
            <Link to="/">
              <Button size="lg" className="bg-gradient-primary hover-glow-primary text-primary-foreground font-semibold">
                Start Playing Now
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AboutPage;
